import React from "react";
import { useApp } from "../../contexts/AppContext";
import styles from "./Tabs.module.css";

interface TabsProps {
  activeTab: string | null;
  onTabChange: (tabId: string | null) => void;
}

export default function Tabs({ activeTab, onTabChange }: TabsProps) {
  const { state } = useApp();
  const tabsContainerRef = React.useRef<HTMLDivElement>(null);

  // Set first tab as active by default
  React.useEffect(() => {
    if (!activeTab && state.tabs && state.tabs.length > 0) {
      onTabChange(state.tabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, state.tabs]);

  // Handle horizontal scroll with mouse wheel
  React.useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle wheel events if the container can scroll horizontally
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!state.tabs || state.tabs.length === 0) {
    return null;
  }

  const getTabName = (tab: any) => {
    if (state.language === "en") {
      return tab.name?.en || tab.name?.ru || String(tab.id || "");
    }
    return tab.name?.ru || tab.name?.en || String(tab.id || "");
  };

  return (
    <div className={styles.tabsContainer} ref={tabsContainerRef}>
      {state.tabs.map((tab) => {
        const name = getTabName(tab);
        return (
          <button
            key={tab.id}
            className={
              activeTab === tab.id ? styles.tabItemActive : styles.tabItem
            }
            onClick={() => onTabChange(tab.id)}
            aria-label={name}
          >
            <span>{name}</span>
          </button>
        );
      })}
    </div>
  );
}
